import type { DrawingDocument } from '../model';
import { isChemicalBond } from './documentSelectors.ts';
import { FUNCTIONAL_GROUPS } from '../chemistry/functionalGroups.ts';

export type ContractedGroups = NonNullable<DrawingDocument['depiction']['contractedGroups']>;

/** #736 step 2: a contracted group as the drawing has it now -- valid, with what its label reads. */
export interface ContractedGroupView {
  readonly attachmentId: string;
  readonly group: string;
  /** The group's other atoms: drawn as the label, not one by one. */
  readonly atomIds: ReadonlySet<string>;
  /** Its bonds among themselves and to the attachment: hidden with them. */
  readonly edgeIds: ReadonlySet<string>;
  /** The label, turned to face its bond: CF₃ with the bond on its left, F₃C with it on its right. */
  readonly label: string;
}

/** The chemical neighbours of `atomId`. */
function neighboursOf(document: DrawingDocument, atomId: string): string[] {
  return document.structure.graph.edges.flatMap((edge) => {
    if (!isChemicalBond(document.structure.bonds[edge.id])) return [];
    if (edge.source === atomId) return [edge.target];
    if (edge.target === atomId) return [edge.source];
    return [];
  });
}

/**
 * The atoms hanging off `attachmentId` away from `restId` (its bond to the rest of the molecule;
 * none for a group on its own) -- the attachment itself left out. A ring through the bond means
 * nothing hangs off it alone: `null`.
 */
export function hangingAtoms(
  document: DrawingDocument,
  attachmentId: string,
  restId: string | undefined
): Set<string> | null {
  const seen = new Set<string>([attachmentId]);
  const queue = [attachmentId];
  while (queue.length) {
    const atom = queue.pop()!;
    for (const next of neighboursOf(document, atom)) {
      if (atom === attachmentId && next === restId) continue;
      if (next === restId) return null; // back round to the rest: a ring
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
  seen.delete(attachmentId);
  return seen;
}

/**
 * #736 step 2: the drawing's contracted groups that still stand -- their atoms exactly what hangs
 * off the attachment. One whose atoms were deleted, bonded elsewhere or added to is left out, so its
 * atoms are drawn: a contracted group never hides chemistry it no longer stands for.
 */
export function contractedGroupViews(document: DrawingDocument): ContractedGroupView[] {
  const entries = Object.entries(document.depiction.contractedGroups ?? {});
  if (entries.length === 0) return [];
  const { coordinates } = document.depiction.coordinateSet;
  const views: ContractedGroupView[] = [];
  for (const [attachmentId, entry] of entries) {
    const definition = FUNCTIONAL_GROUPS.find((group) => group.key === entry.group);
    const at = coordinates[attachmentId];
    if (!definition || !at || !document.structure.atoms[attachmentId]) continue;
    const members = new Set(entry.atomIds);
    const outside = neighboursOf(document, attachmentId).filter((id) => !members.has(id));
    if (outside.length > 1) continue;
    const rest = outside[0];
    const hanging = hangingAtoms(document, attachmentId, rest);
    if (!hanging || hanging.size !== members.size || [...members].some((id) => !hanging.has(id))) continue;
    const edgeIds = new Set(
      document.structure.graph.edges
        .filter(
          (edge) =>
            (members.has(edge.source) || edge.source === attachmentId) &&
            (members.has(edge.target) || edge.target === attachmentId)
        )
        .map((edge) => edge.id)
    );
    const restAt = rest ? coordinates[rest] : undefined;
    views.push({
      attachmentId,
      group: entry.group,
      atomIds: members,
      edgeIds,
      label: restAt && restAt.x > at.x + 1e-6 ? definition.reversed : definition.label
    });
  }
  return views;
}

/** #736 step 2: what the paper and an export draw instead of the contracted groups' atoms. */
export function contractedDisplay(document: DrawingDocument): {
  readonly hiddenAtomIds: ReadonlySet<string>;
  readonly hiddenEdgeIds: ReadonlySet<string>;
  readonly labels: Readonly<Record<string, string>>;
} {
  const views = contractedGroupViews(document);
  return {
    hiddenAtomIds: new Set(views.flatMap((view) => [...view.atomIds])),
    hiddenEdgeIds: new Set(views.flatMap((view) => [...view.edgeIds])),
    labels: Object.fromEntries(views.map((view) => [view.attachmentId, view.label]))
  };
}

/** #736 step 2: `document` with these groups recorded as contracted (attachment -> its group). */
export function withContractedGroups(
  document: DrawingDocument,
  groups: readonly { readonly attachmentId: string; readonly group: string; readonly atomIds: readonly string[] }[]
): DrawingDocument {
  if (groups.length === 0) return document;
  const contractedGroups: Record<string, ContractedGroups[string]> = { ...(document.depiction.contractedGroups ?? {}) };
  for (const { attachmentId, group, atomIds } of groups) {
    const [first, ...rest] = atomIds;
    if (first) contractedGroups[attachmentId] = { group, atomIds: [first, ...rest] };
  }
  return { ...document, depiction: { ...document.depiction, contractedGroups } };
}

/** #736 step 2: `document` without these attachments' contracted-group entries. */
export function withoutContractedGroups(
  document: DrawingDocument,
  attachmentIds: ReadonlySet<string>
): DrawingDocument {
  const current = document.depiction.contractedGroups;
  if (!current || ![...attachmentIds].some((id) => id in current)) return document;
  const { contractedGroups: _old, ...depiction } = document.depiction;
  const kept = Object.fromEntries(Object.entries(current).filter(([id]) => !attachmentIds.has(id)));
  return {
    ...document,
    depiction: Object.keys(kept).length ? { ...depiction, contractedGroups: kept } : depiction
  };
}

/** The valid contracted groups' attachments among `atomIds` (or all, when absent). */
export function contractedAttachments(document: DrawingDocument, atomIds?: ReadonlySet<string>): string[] {
  return contractedGroupViews(document)
    .filter((view) => !atomIds || atomIds.has(view.attachmentId) || [...view.atomIds].some((id) => atomIds.has(id)))
    .map((view) => view.attachmentId);
}
